import { Component, DestroyRef, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { CommonFunctions } from '@shared/common-functions';
import { CONTACT_ERR_MSG, EMAIL_VALIDATION_PATTERN, Invalid_IMAGE_FORMAT, MULTILINGUAL_NAME_VALIDATION_PATTERN, NO_IMAGE, USER_ROLES } from '@shared/constant/shared.constant';
import { FileResponse } from '@core/models/file-response.model';
import { DialogControlService } from '@shared/services/dialog-control.service';
import { ToastService } from '@shared/services/toast.service';
import { CountryISO, PhoneNumberFormat, SearchCountryField } from 'ngx-intl-tel-input-gg';
import { ProfileService } from '@core/services/profile.service';
import { SuperAdminService } from '@core/services/super-admin.service';
import { autoUnsubWith } from '@shared/services/utility.abstract';
import { EncryptionStorageService } from '@core/services/encryption-storage.service';
import { ConfigService } from '@core/services/config.service';

@Component({
  selector: 'app-driver-basic-information',
  templateUrl: './driver-basic-information.component.html',
  styleUrls: ['./driver-basic-information.component.scss']
})
export class DriverBesicInformationComponent implements  OnChanges {
  readonly destroyRef = inject(DestroyRef);
  @Input() isProfileImgError: boolean = false;
  @Input() showInput: boolean = false;
  @Input() imageUrl: string;
  @Input() driverDetails: any;
  @Output() driverBesicData = new EventEmitter<string>();
  driverUpdateForm: FormGroup;
  NAME_MAX_LENGTH: number = 50;
  dummy = NO_IMAGE;
  searchCountryField = SearchCountryField;
  countryISO: CountryISO = CountryISO.UnitedStates;
  phoneNumberFormat = PhoneNumberFormat;
  preferredCountries: CountryISO[] = [CountryISO.UnitedStates, CountryISO.UnitedKingdom];
  separateDialCode = false;
  formData: FormData = new FormData();
  allowedExtensions = ['jpg', 'jpeg', 'png'];
  role: string;
  userRoles = USER_ROLES;
  requiredLastNameAsterisk: boolean = false;

   hasValidCredits(): boolean {
    return this.driverDetails?.userCredits?.some(c => c.amount > 0) ?? false;
  }

constructor(
    private readonly fb: FormBuilder,
    public readonly dialogControlService: DialogControlService,
    private readonly superAdminService: SuperAdminService,
    private readonly toastService: ToastService,
    private readonly service: ProfileService,
    private readonly encryptStorage: EncryptionStorageService,
    private readonly configService: ConfigService
  ) {
    this.role = this.encryptStorage.getItem('roleName');
    const { validators, required } = CommonFunctions.getLastNameValidators('driver', this.configService);
    this.requiredLastNameAsterisk = required;
    this.driverUpdateForm = this.fb.group({
      id: [''],
      firstName: ['', [Validators.required, Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(this.NAME_MAX_LENGTH), CommonFunctions.noLeadingSpacesValidator()]],
      lastName: ['', [...validators,Validators.pattern(MULTILINGUAL_NAME_VALIDATION_PATTERN), Validators.minLength(3), Validators.maxLength(this.NAME_MAX_LENGTH),  CommonFunctions.noLeadingSpacesValidator()]],
      phone: ['',[Validators.required]],
      email: ['', [Validators.required, Validators.pattern(EMAIL_VALIDATION_PATTERN)]],
      profileImagePath: ['']
    });
  }

  ngOnChanges() {
    if (this.showInput) {
      this.patchForm(this.driverDetails)
    }
  }

  triggerFileInput() {
    const fileInput = document.getElementById('fileInput') as HTMLElement;
    fileInput.click();
  };

  getFlagClass(iso: string): string {
    const lowerCaseIso = iso?.toLowerCase();
    return `fi fi-${lowerCaseIso}`;
  };

  clearImage() {
    this.imageUrl = null;
    this.driverUpdateForm.get('profileImagePath')?.setValue(null);
    this.formData.delete('Images');
    this.isProfileImgError = false;
    const fileInput = document.getElementById('fileInput') as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

  }

  onImageSelected(event: any) {
    const file = event.target.files[0];
    const maxSize = 5 * 1024 * 1024;
    const fileExtension = file.name.split('.').pop()?.toLowerCase();
    this.formData.append('Images', file);

    if (!fileExtension || !this.allowedExtensions.includes(fileExtension)) {
      this.toastService.showToastMessage('error', 'Error', Invalid_IMAGE_FORMAT);
      this.formData.delete('Images');
      return false;
    } else if (file.size > maxSize && (fileExtension || this.allowedExtensions.includes(fileExtension))) {
      this.toastService.showToastMessage('error', 'Error', 'File size should be less than 5MB.');
      this.formData.delete('Images');
      return false;
    }

    if (file.size < maxSize && (fileExtension || this.allowedExtensions.includes(fileExtension))) {

      this.service.imageUplaod(this.formData)
        .pipe(autoUnsubWith(this.destroyRef)).subscribe({
          next: (response: FileResponse) => {
            this.formData.delete('Images')
            const data = response;
            for (const key in data) {
              if (data.hasOwnProperty(key)) {
                const url = data[key];
                this.imageUrl = url;
                this.driverUpdateForm.get('profileImagePath')?.setValue(url);

              }
            }
          }, error: (_error) => {
            this.isProfileImgError = false;
          }
        });
    }

    return true;
  };
  patchForm(driverDetails: any) {
    this.countryISO = driverDetails?.iso ?? 'us';
    if (driverDetails) {
      this.driverUpdateForm.patchValue({
        firstName: driverDetails.firstName || '',
        lastName: driverDetails.lastName || '',
        phone: driverDetails.mobileNumber || '',
        email : driverDetails.email || '',
        profileImagePath: driverDetails.profileImagePath || "",

      })

    }
  };
  calcelDriverInfoEdit(driverDetails) {
    this.showInput = false;
    this.superAdminService.setShowInput(false);
    this.driverUpdateForm.reset();
    this.patchForm(driverDetails);
    this.imageUrl = driverDetails.profileImagePath;
  };

  updateDriverInformation() {
    if (!this.driverUpdateForm.value.email && !this.driverUpdateForm.value.phone) {
      this.toastService.showToastMessage('error', 'Error', CONTACT_ERR_MSG);
      return;
    }
    if(this.driverUpdateForm.valid){
      this.driverBesicData.emit(this.driverUpdateForm.value)
    }else{
      this.driverUpdateForm.markAllAsTouched();
    }

  }
   onlyNumberInput(event: KeyboardEvent): void {
    CommonFunctions.onlyNumberInput(event);
  }
}
